<template>
  <div>
    <ts-loading-banner :loading="loading">
      <div class="row form-group">
        <div class="col-md-12">
          <label class="required ts-text-xs">{{
            $t('studentScoringEntry.advisor')
            }}</label>
          <select class="form-control" v-model="model.advisor_id" :disabled="disableAdvisor">
            <option :value="1">{{ $t('studentScoringEntry.teacher') }}</option>
            <option :value="2">
              {{ $t('studentScoringEntry.management') }}
            </option>
            <option :value="3">{{ $t('studentScoringEntry.parent') }}</option>
          </select>
          <div class="validate" v-if="errors.has('advisor_id')">
            {{ errors.first('advisor_id') }}
          </div>
        </div>
      </div>
      <div class="row form-group">
        <div class="col-md-12">
          <label class="required ts-text-xs">{{
            $t('studentScoringEntry.comment')
            }}</label>
          <ts-button outline class="ts-ml-6 ts-mb-2 ts-text-xs" color="info" @click.prevent="onClickComment">{{
            $t('studentScoringEntry.clickComment') }}</ts-button>
          <VueEditor v-model="model.comments" :editor-toolbar="customToolbar" :disabled="disableComment"
            :class="{ 'ts-bg-gray-100': disableComment }" />
          <p v-if="!isOver()">
            You have {{ charactersRemaining }} characters remaining.
          </p>
          <p v-else class="over ts-text-red-700">
            You are {{ charactersOver }} characters over the limit.
          </p>
          <div class="validate" v-if="errors.has('comments')">
            {{ errors.first('comments') }}
          </div>
        </div>
      </div>
      <a-modal v-if="clickComment" v-model="clickComment" :closable="false" width="50%"
        :title="$t('studentScoringEntry.clickComment')">
        <ClickComment v-model="model" :comments="comments" />
        <template v-slot:footer>
          <ts-button color="primary" @click="() => (clickComment = false)">{{
            $t('ok')
            }}</ts-button>
        </template>
      </a-modal>
      <div class="ts-flex ts-justify-end">
         <ts-button color="primary" @click.prevent="onSave">{{
              $t('save')
            }}</ts-button>
      </div>
    </ts-loading-banner>
  </div>
</template>

<script>
import { Errors } from 'form-backend-validation'
import { VueEditor } from 'vue2-editor'
import { formViewModel } from '@/api/student-scoring-table-entry'
import ClickComment from './click-comment.vue'
import {
  saveComment
} from '@/api/student-scoring-table-entry'

export default {
  name: 'studentScoreComment',
  components: { VueEditor, ClickComment },
  props: ['value', 'scoringCycleId', 'cycleValue', 'payload'],
  data() {
    return {
      errors: new Errors(),
      loading: false,
      clickComment: false,
      comments: [],
      model: {},
      maxCharacters: 500,
      customToolbar: [
        [{ font: [] }],
        [{ header: [false, 1, 2, 3, 4, 5, 6] }],
        ['bold', 'italic', 'underline', 'strike'],
        [
          { align: '' },
          { align: 'center' },
          { align: 'right' },
          { align: 'justify' }
        ],
        [{ header: 1 }, { header: 2 }],
        ['blockquote', 'code-block'],
        [{ list: 'ordered' }, { list: 'bullet' }, { list: 'check' }]
      ]
    }
  },
  computed: {
    charactersRemaining: function () {
      const len = this.model && this.model.comments ? this.model.comments.length : 0
      return this.maxCharacters - len
    },
    charactersOver: function () {
      const len = this.model && this.model.comments ? this.model.comments.length : 0
      return this.isOver() ? len - this.maxCharacters : 0
    },
    isAdmin() {
      if (!this.$store.state.authUser) {
        return false
      }
      const userName = (this.$store.state.authUser.user_name || '').toLowerCase()
      if (userName === 'admin' || userName === 'superadmin' || userName === 'techstore') {
        return true
      }
      if (this.$store.state.authUser.roles && Array.isArray(this.$store.state.authUser.roles)) {
        if (this.$store.state.authUser.roles.some(r => r.name === '*' || (r.name && r.name.toLowerCase() === 'admin'))) {
          return true
        }
      }
      if (this.$store.getters.authPermissionAllow && (this.$store.getters.authPermissionAllow('*') || this.$store.getters.authPermissionAllow('view-staff-users'))) {
        return true
      }
      return Boolean(this.$store.getters.isStaffLogin)
    },
    disableAdvisor() {
      return !this.isAdmin
    },
    disableComment() {
      if (this.isAdmin) {
        return false
      }
      return !this.$store.getters.authPermissionAllow('update-score-comments')
    },
  },
  created() {
    this.model = this.value
    if (!this.model.advisor_id) {
      this.model.advisor_id = 1
    }
    this.fetchComment()
    this.getComment()
  },
  methods: {
    isOver: function () {
      return this.charactersRemaining < 0
    },
    onClickComment() {
      this.clickComment = true
    },
    getComment() {
      this.loading = true
      this.$store
        .dispatch('studentScoringEntry/getComment')
        .then(response => {
          this.loading = false
          const comments = this.model.comments || ''
          this.comments = response.data.map(el => ({
            value: el.comment_id,
            label: el.comment,
            checked: comments.includes(el.comment)
          }))
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
    },
    fetchComment() {
      this.loading = true
      formViewModel({
        params: {
          fnName: 'getComment',
          student_id: this.value.student_id,
          scoring_cycle_id: this.payload.scoring_cycle_id,
          cycle_value: this.cycleValue,
          st_class_id: this.value.st_class_id
        }
      })
        .then(({ data }) => {
          if (data.getComment) {
            const { advisor_id, comments, sc_stu_com_id } = data.getComment

            if (comments == this.model.comments || this.model.comments == '') {
              this.model.comments = comments
            }
            this.model.advisor_id = advisor_id ? parseInt(advisor_id) : (this.model.advisor_id || 1)
            this.model.sc_stu_com_id = sc_stu_com_id
          } else if (!this.model.advisor_id) {
            this.model.advisor_id = 1
          }
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.loading = false
        })
    },
    onSave() {
      this.loading = true
      const objectData = Object.assign({}, this.model)
      delete objectData.scores;
      saveComment({
        ...objectData,
        class_id: this.payload.class_id,
        scoring_cycle_id: this.payload.scoring_cycle_id,
        cycle_value: this.payload.cycle_value
      })
        .then(() => this.fetchComment())
        .then(() => this.$emit('close'))
        .catch(error => {
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => (this.loading = false))
      this.loading = true
    }
  }
}
</script>
